import React, { useEffect, useMemo, useState } from 'react';
import { EditIcon, SearchIcon } from 'tdesign-icons-react';
import { AutoComplete, Form, Link, Table } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import SvgIcon from '@/components/SvgIcon';
import useTable from '@/hooks/useTable';
import { isVoid, sleep } from '@/utils/common';
import HideReadOnlyAppend from './HideReadOnlyAppend';
import ConfigModal from './ConfigModal';

export default function Configuration({
  noHide,
  loading,
  brokerHost,
  clusterName,
  updateBrokerService,
  configListService,
  noOperate,
}) {
  const [hideReadOnlyConfig, setHideReadOnlyConfig] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [visible, setVisible] = useState(false);
  const [configItems, setconfigItems] = useState([]);

  const columns = [
    {
      colKey: 'configItem',
      title: '配置名',
      cell: ({ row }) => (
        <span>
          {row.configItem}
          {row.readOnlyConfig && (
            <SvgIcon
              type='lock-closed'
              color='var(--td-brand-color-7)'
              style={{ marginBottom: 3, marginLeft: 3 }}
            />
          )}
        </span>
      ),
      sorter: true,
    },
    {
      colKey: 'configExplanation',
      title: '描述',
    },
    {
      colKey: 'configValue',
      title: '配置值',
    },
    noOperate
      ? null
      : {
          colKey: 'option',
          title: '操作',
          fixed: 'right',
          align: 'center',
          cell: ({ row }) =>
            row.readOnlyConfig ? null : (
              <Link
                theme='primary'
                hover='color'
                onClick={() => {
                  setVisible(true);
                  setCurrentRow(row);
                }}
              >
                <EditIcon />
              </Link>
            ),
          width: 60,
        },
  ].filter((item) => !isVoid(item));

  const loadData = useMemoizedFn(async (params) => {
    const configItem = params?.configItem || '';
    const { data: items } = await configListService();

    const filteredItems = items.filter((item) => {
      const isFound = item.configItem.toLowerCase().includes(configItem.toLowerCase());
      if (hideReadOnlyConfig) {
        return isFound && item.readOnlyConfig === false;
      }
      return isFound;
    });
    setconfigItems(filteredItems);

    setData(filteredItems);
  });

  const onHideReadOnlyConfig = async (val) => {
    setHideReadOnlyConfig(val);
    await sleep(30);
    doSearch(null, true);
  };

  const options = useMemo(() => {
    return configItems.map((item) => ({
      label: item.configItem,
      value: item.configItem,
    }));
  }, [configItems]);

  const { config, header, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    append: noHide ? null : (
      <HideReadOnlyAppend value={hideReadOnlyConfig} onChange={onHideReadOnlyConfig} />
    ),
    search: (
      <Form.FormItem name='configItem'>
        <AutoComplete
          style={{ width: 260, marginBottom: 26 }}
          popupProps={{ overlayInnerStyle: { width: 'auto' } }}
          options={options}
          placeholder='请输入配置名'
          clearable
          inputProps={{
            suffixIcon: <SearchIcon />,
          }}
        />
      </Form.FormItem>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const handleClose = (refresh) => {
    setVisible(false);
    setCurrentRow({});
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <>
      {header}
      <Table {...config} loading={loading} />
      <ConfigModal
        visible={visible}
        detail={currentRow}
        onClose={handleClose}
        brokerHost={brokerHost}
        clusterName={clusterName}
        updateBrokerService={updateBrokerService}
      />
    </>
  );
}
